import type { Advisor } from '@/types'

import Link from 'next/link'
import { CalendarClock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PillButton } from '@/components/generals/pill-button'
import { RatingChip } from '@/components/generals/rating-chip'
import { WashedPortrait } from '@/components/generals/washed-portrait'
import { NextAvailable } from '@/components/landing/advisors/next-available'

interface AdvisorCardProps {
    advisor: Advisor
    className?: string
    compact?: boolean
}

/** Tall, full-colour advisor portrait that reveals a short bio on hover. */
export const AdvisorCard = ({ advisor, className, compact = false }: AdvisorCardProps) => {
    return (
        <article className={cn('grain group relative isolate overflow-hidden rounded-[30px] bg-ink', compact ? 'aspect-[3/4]' : 'aspect-[3/4.25]', className)}>
            {advisor.portrait
                ? (
                    <WashedPortrait
                        src={advisor.portrait}
                        alt={advisor.portraitAlt}
                        wash={advisor.washColor}
                        intensity='medium'
                        sizes='(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 92vw'
                        imageClassName='group-hover:scale-[1.05]'
                    />
                )
                : <div aria-hidden='true' className='absolute inset-0' style={{ backgroundImage: 'radial-gradient(120% 90% at 50% 0%, #3a2a1e, #0b0908 70%)' }} />}

            <div aria-hidden='true' className='absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.25)_0%,rgba(0,0,0,0)_28%,rgba(0,0,0,0.25)_50%,rgba(0,0,0,0.92)_100%)]' />
            <div aria-hidden='true' className='absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_15%,rgba(8,6,4,0.7)_45%,rgba(8,6,4,0.95)_100%)] opacity-0 transition-opacity duration-700 ease-cinema group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100' />
            <div aria-hidden='true' className='hairline pointer-events-none absolute inset-0 rounded-[inherit]' />

            <div className='absolute inset-x-4 top-4 z-10 flex items-center justify-between gap-2'>
                <RatingChip rating={advisor.rating} reviews={compact ? undefined : advisor.reviewCount} />

                <span className='glass rounded-full px-2.5 py-1 text-xs text-cream/85'>
                    {advisor.yearsExperience} years
                </span>
            </div>

            <div className='absolute inset-x-0 bottom-0 p-5 [text-shadow:0_1px_12px_rgba(0,0,0,0.55)] sm:p-6'>
                <p className='text-[10.5px] font-medium uppercase tracking-[0.28em] text-cream/60'>
                    {advisor.specialty}
                </p>

                <h3 className='mt-2 font-serif text-[2rem] italic leading-[1.02] text-cream'>
                    {advisor.name}
                </h3>

                <p className='mt-1 text-xs text-cream/55'>
                    {advisor.title}
                </p>

                {!compact && (
                    <div className='grid grid-rows-[0fr] transition-[grid-template-rows] duration-700 ease-cinema group-hover:grid-rows-[1fr] group-focus-within:grid-rows-[1fr] [@media(hover:none)]:grid-rows-[1fr]'>
                        <div className='overflow-hidden'>
                            <p className='pt-4 text-[13px] leading-relaxed text-cream/75'>
                                {advisor.shortBio}
                            </p>

                            <p className='mt-3 flex items-center gap-2 text-xs text-citron'>
                                <CalendarClock aria-hidden='true' className='size-3.5 shrink-0' />
                                <span>
                                    Next opening: <NextAvailable advisor={advisor} />
                                </span>
                            </p>
                        </div>
                    </div>
                )}

                <div className='relative z-20 mt-5 flex items-center justify-between gap-3'>
                    <PillButton href={`/advisors/${advisor.slug}#book`} size='sm'>
                        Schedule a call
                    </PillButton>

                    <span className='text-xs text-cream/50 transition-colors group-hover:text-cream/80'>
                        {advisor.location}
                    </span>
                </div>
            </div>

            <Link href={`/advisors/${advisor.slug}`} className='absolute inset-0 z-[15] rounded-[inherit] focus-visible:outline-offset-[-4px]'>
                <span className='sr-only'>
                    Read the full profile of {advisor.name}, {advisor.specialty} advisor
                </span>
            </Link>
        </article>
    )
}
